import moment, { Moment } from 'moment';
import { MANDALA_GATE_SEQUENCE_CCW } from '../../chart/mandala/mandala.constants';
import { HdPlanetKey } from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { legendCategoryOfKey } from '../transit-legend.utils';
import { HdTransitSegment, HdTransitStation, TransitGranularity } from '../transit.models';
import { HdTransitRetrogradeSpan, clipRetrogradeSpans, isRetrogradeThroughout } from '../transit-retrograde.utils';
import {
  HdEphemerisBoundaryMarker,
  HdEphemerisClippedSegment,
  HdEphemerisGateBlock,
  HdEphemerisGateEpoch,
  HdEphemerisLabelParts,
  HdEphemerisLabeledBlock,
  HdEphemerisLineBlock,
  HdEphemerisMergedBlock,
  HdEphemerisPxSpan,
  HdEphemerisRange,
  HdEphemerisRenderItem,
  HdEphemerisRetroLoopSpan,
  HdEphemerisRulerTick,
  HdEphemerisStationMarker,
  HdEphemerisYearBox,
  HdEphemerisYearDatesTier,
  HdEphemerisYearTitleTier,
  HdEphemerisZoomConfig,
  HdEphemerisZoomLevel,
} from './hd-ephemeris-lanes.models';

/**
 * Pure projectie-functies voor `hd-ephemeris-lanes` (fase F3) — het hart van
 * de component en van de unit-tests. Geen DOM, geen Angular: alles rekent op
 * epoch-ms en px in de coördinaatruimte van de geladen range.
 *
 * Pipeline per lane (zie de component): `clipSegmentsToWindow` →
 * `projectSegmentsToBlocks` → per container `adaptLabels` + `mergeTinyBlocks`
 * op de lijn-items → `adaptLabels` + `mergeTinyBlocks` op de containers →
 * `flattenGateBlocks`. Retro-lussen en stations lopen apart mee. De retrograde-
 * arcering komt uit de exacte perioden (`retrogradeSpans`, station tot station),
 * nooit uit de segment-vlag: die hoort enkel bij het midden van een segment.
 * `computeRulerTicks` voedt de datum/tijd-ruler boven/onder de lanes.
 */

/**
 * Selecteerbare zoom-niveaus. Maand staat bewust niet in de lijst: het venster
 * (548 dagen) plus de basis-/laadmarge overschrijdt het API-maximum van 5 jaar
 * voor poort-granulariteit, waardoor elke load faalt. Config en ruler-stappen
 * voor 'month' blijven bestaan zodat het niveau na een backend-optimalisatie
 * enkel hier terug toegevoegd hoeft te worden.
 */
export const HD_EPHEMERIS_ZOOM_LEVELS: readonly HdEphemerisZoomLevel[] = ['hour', 'day', 'week'];
/** Default zoom-niveau (besluit 13). */
export const HD_EPHEMERIS_DEFAULT_ZOOM: HdEphemerisZoomLevel = 'day';
/**
 * De jaren-weergave: los van {@link HD_EPHEMERIS_ZOOM_LEVELS} omdat ze enkel
 * kiesbaar is zolang alleen de transpersoonlijke planeten zichtbaar zijn
 * (zie {@link yearZoomAvailable}); ze wordt ook nooit als setting bewaard.
 */
export const HD_EPHEMERIS_YEAR_ZOOM: Extract<HdEphemerisZoomLevel, 'year'> = 'year';

const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;
const MS_PER_YEAR = 365.25 * MS_PER_DAY;

/**
 * Fractie van de viewport (vanaf links) waar het anker (de kijktijd, of een
 * marker bij een legende-klik) komt te staan: het golden-ratio-punt — je ziet
 * iets meer toekomst dan verleden.
 */
export const HD_EPHEMERIS_ANCHOR_FRACTION = 0.382;

// ── Zoom-tuning (besluit 13: startwaarden, bewust als constanten vastgelegd) ──
/** Zichtbare vensters rond de kijk-tijd per niveau: 48 uur / 2 weken / ~3 maanden / ~1,5 jaar. */
export const HD_EPHEMERIS_WINDOW_HOURS_AT_HOUR_ZOOM = 48;
export const HD_EPHEMERIS_WINDOW_DAYS_AT_DAY_ZOOM = 14;
export const HD_EPHEMERIS_WINDOW_DAYS_AT_WEEK_ZOOM = 92;
export const HD_EPHEMERIS_WINDOW_DAYS_AT_MONTH_ZOOM = 548;

/** Px-dichtheid per niveau — de tuningknoppen voor de leesbaarheid. */
export const HD_EPHEMERIS_PX_PER_HOUR_AT_HOUR_ZOOM = 48;
export const HD_EPHEMERIS_PX_PER_DAY_AT_DAY_ZOOM = 240;
export const HD_EPHEMERIS_PX_PER_DAY_AT_WEEK_ZOOM = 24;
export const HD_EPHEMERIS_PX_PER_DAY_AT_MONTH_ZOOM = 8;

// ── Jaren-weergave (globale cycli: poortwissels van Uranus, Neptunus en Pluto) ──
/**
 * Schaal van het lesvoorbeeld (Pluto/Neptunus 2002–2030): 55 px per jaar, dus
 * ±29 jaar in de grote transits-dialog (±1600 px lanes-breedte op FHD).
 */
export const HD_EPHEMERIS_PX_PER_YEAR_AT_YEAR_ZOOM = 55;
export const HD_EPHEMERIS_WINDOW_YEARS_AT_YEAR_ZOOM = 29;
/**
 * "Nu" staat in het midden van het beeld (Steven 2026-10-21: evenveel jaren terug als vooruit; eerst
 * stond nu rechts, op 0,86, zodat het lesvoorbeeld 2002–2030 in beeld kwam).
 */
export const HD_EPHEMERIS_YEAR_ANCHOR_FRACTION = 0.5;
/** Eerste lading = het beeld + 1 jaar aan elke kant: snel iets te zien, de rest volgt op de achtergrond. */
export const HD_EPHEMERIS_YEAR_BASE_MARGIN_YEARS = 1;
/**
 * Bijladen zodra het beeld binnen 5 jaar van de geladen rand komt, en dan tot
 * 15 jaar voorbij het beeld: na de eerste lading staat er aan elke kant ±15 jaar
 * klaar, dus scrollen gaat een heel eind zonder te wachten.
 */
export const HD_EPHEMERIS_YEAR_LOAD_MARGIN_YEARS = 5;
export const HD_EPHEMERIS_YEAR_PREFETCH_YEARS = 15;
/**
 * Langste afwezigheid binnen één poortperiode. Een trage planeet die retrograde
 * terug de vorige poort in gaat (of al even de volgende in), komt binnen het
 * jaar terug: de retrograde boog (Uranus ±4°, Neptunus ±2,5°, Pluto ±2°) is
 * kleiner dan een poort (5,625°). Een echte terugkeer naar dezelfde poort komt
 * pas na een volle omloop (84+ jaar).
 */
export const HD_EPHEMERIS_GATE_EPOCH_MAX_GAP_MS = 400 * MS_PER_DAY;

/** Dekking van de ephemeris (spec: 1800–2399); de max is exclusief (2400-01-01). */
export const HD_EPHEMERIS_DOMAIN_MIN_MS = Date.UTC(1800, 0, 1);
export const HD_EPHEMERIS_DOMAIN_MAX_MS = Date.UTC(2400, 0, 1);

/** Het initieel geladen bereik is de zoom-vensterbreedte × deze factor, gecentreerd op het anker. */
export const HD_EPHEMERIS_BASE_RANGE_FACTOR = 3;
/** Komt het zichtbare venster binnen deze marge (× vensterbreedte) van de geladen horizon, dan wordt bijgeladen. */
export const HD_EPHEMERIS_LOAD_MARGIN_FACTOR = 0.5;

// ── Label-drempels (drie-traps: `poort.lijn` → `.lijn` → samengevoegd "…") ──
// Geschat op 10px SVG-tekst: "99.9" ≈ 20px en ".9" ≈ 9px, plus ademruimte.
export const HD_EPHEMERIS_FULL_LABEL_MIN_PX = 26;
export const HD_EPHEMERIS_SHORT_LABEL_MIN_PX = 12;
export const HD_EPHEMERIS_MERGE_MIN_PX = 10;
/**
 * Week-view: lijnblokjes hebben er geen volle variant (`poort.lijn` past niet)
 * en worden niet samengevoegd — elk blokje dat het smalle `.lijn`-label
 * minimaal kan dragen (≈ de tekstbreedte zelf) krijgt het ook.
 */
export const HD_EPHEMERIS_WEEK_LINE_LABEL_MIN_PX = 8;

// ── Lane-metriek (px): verticale layout van één lane ──
export const HD_EPHEMERIS_LANE_HEIGHT = 56;
export const HD_EPHEMERIS_GATE_Y_PX = 4;
export const HD_EPHEMERIS_GATE_HEIGHT_PX = 40;
export const HD_EPHEMERIS_LINE_HEIGHT_PX = 18;
/** Lijn-items staan onderaan de gate-container. */
export const HD_EPHEMERIS_LINE_Y_PX = HD_EPHEMERIS_GATE_Y_PX + HD_EPHEMERIS_GATE_HEIGHT_PX - HD_EPHEMERIS_LINE_HEIGHT_PX;
export const HD_EPHEMERIS_GATE_LABEL_Y_PX = HD_EPHEMERIS_GATE_Y_PX + 15;
/** Strip onder de containers waarin de retro-lus-boog hangt. */
export const HD_EPHEMERIS_LOOP_Y_PX = HD_EPHEMERIS_GATE_Y_PX + HD_EPHEMERIS_GATE_HEIGHT_PX + 3;
export const HD_EPHEMERIS_LOOP_DEPTH_PX = 6;

// ── Lane-metriek jaren-weergave (px): twee rijen poortperiode-blokken ──
export const HD_EPHEMERIS_YEAR_BOX_HEIGHT_PX = 34;
export const HD_EPHEMERIS_YEAR_ROW_GAP_PX = 4;
export const HD_EPHEMERIS_YEAR_LANE_PADDING_PX = 5;
export const HD_EPHEMERIS_YEAR_LANE_HEIGHT =
  2 * HD_EPHEMERIS_YEAR_LANE_PADDING_PX + 2 * HD_EPHEMERIS_YEAR_BOX_HEIGHT_PX + HD_EPHEMERIS_YEAR_ROW_GAP_PX;
/** Baseline van de titel ("Poort 26") en van de datumregel binnen een blok. */
export const HD_EPHEMERIS_YEAR_TITLE_Y_PX = 14;
export const HD_EPHEMERIS_YEAR_DATES_Y_PX = 27;
// Labeldrempels (blokbreedte in px), geschat op 11px-titel en 9px-datums.
export const HD_EPHEMERIS_YEAR_TITLE_FULL_MIN_PX = 64;
export const HD_EPHEMERIS_YEAR_TITLE_SHORT_MIN_PX = 18;
export const HD_EPHEMERIS_YEAR_DATES_FULL_MIN_PX = 124;
export const HD_EPHEMERIS_YEAR_DATES_SHORT_MIN_PX = 104;
export const HD_EPHEMERIS_YEAR_DATES_YEARS_MIN_PX = 62;

// ── Ruler-metriek (px): hoogte van de datum/tijd-stroken boven/onder de lanes ──
export const HD_EPHEMERIS_RULER_HEIGHT_PX = 24;
/** Minimale px-afstand tussen gelabelde ruler-ticks; eronder kiest de ruler een grovere stap. */
export const HD_EPHEMERIS_RULER_MIN_LABEL_SPACING_PX = 40;

// ── Zoom-config + lineaire tijd↔px-schaal ────────────────────────────────

/**
 * Config van één zoom-niveau. Uur, dag én week vragen lijn-detail: op
 * 24 px/dag is er voor alle planeten behalve de maan ruimte voor lijnblokjes
 * (de view toont daar enkel het smalle `.lijn`-label en filtert de maan-lijnen
 * weg). De service splitst de week-aanvraag in calls ≤ 92 dagen (API-grens
 * voor lijn-granulariteit). Maand blijft poort-detail: op 8 px/dag zijn
 * lijnblokjes onleesbaar en het bereik overschrijdt de API-grens ruim.
 * Jaar is poort-detail over tientallen jaren (de service haalt het in blokken
 * van 5 jaar op), met "nu" in het midden en een eigen laad-ritme.
 */
export function zoomLevelConfig(level: HdEphemerisZoomLevel): HdEphemerisZoomConfig {
  switch (level) {
    case 'hour':
      return classicZoomConfig(
        level,
        HD_EPHEMERIS_WINDOW_HOURS_AT_HOUR_ZOOM * MS_PER_HOUR,
        HD_EPHEMERIS_PX_PER_HOUR_AT_HOUR_ZOOM / MS_PER_HOUR,
        'line',
      );
    case 'day':
      return classicZoomConfig(
        level,
        HD_EPHEMERIS_WINDOW_DAYS_AT_DAY_ZOOM * MS_PER_DAY,
        HD_EPHEMERIS_PX_PER_DAY_AT_DAY_ZOOM / MS_PER_DAY,
        'line',
      );
    case 'week':
      return classicZoomConfig(
        level,
        HD_EPHEMERIS_WINDOW_DAYS_AT_WEEK_ZOOM * MS_PER_DAY,
        HD_EPHEMERIS_PX_PER_DAY_AT_WEEK_ZOOM / MS_PER_DAY,
        'line',
      );
    case 'month':
      return classicZoomConfig(
        level,
        HD_EPHEMERIS_WINDOW_DAYS_AT_MONTH_ZOOM * MS_PER_DAY,
        HD_EPHEMERIS_PX_PER_DAY_AT_MONTH_ZOOM / MS_PER_DAY,
        'gate',
      );
    case 'year':
      return {
        level,
        windowMs: HD_EPHEMERIS_WINDOW_YEARS_AT_YEAR_ZOOM * MS_PER_YEAR,
        pxPerMs: HD_EPHEMERIS_PX_PER_YEAR_AT_YEAR_ZOOM / MS_PER_YEAR,
        granularity: 'gate',
        anchorFraction: HD_EPHEMERIS_YEAR_ANCHOR_FRACTION,
        loadMarginMs: HD_EPHEMERIS_YEAR_LOAD_MARGIN_YEARS * MS_PER_YEAR,
        prefetchMarginMs: HD_EPHEMERIS_YEAR_PREFETCH_YEARS * MS_PER_YEAR,
        baseMarginMs: HD_EPHEMERIS_YEAR_BASE_MARGIN_YEARS * MS_PER_YEAR,
      };
  }
}

/** Uur/dag/week/maand: anker op het golden-ratio-punt, laden tot een half venster voorbij het beeld. */
function classicZoomConfig(
  level: HdEphemerisZoomLevel,
  windowMs: number,
  pxPerMs: number,
  granularity: TransitGranularity,
): HdEphemerisZoomConfig {
  const loadMarginMs = windowMs * HD_EPHEMERIS_LOAD_MARGIN_FACTOR;
  return {
    level,
    windowMs,
    pxPerMs,
    granularity,
    anchorFraction: HD_EPHEMERIS_ANCHOR_FRACTION,
    loadMarginMs,
    prefetchMarginMs: loadMarginMs,
    baseMarginMs: null,
  };
}

/** Lineaire schaal: ms → px relatief aan de oorsprong van de geladen range. */
export function timeToPx(timeMs: number, originMs: number, config: HdEphemerisZoomConfig): number {
  return (timeMs - originMs) * config.pxPerMs;
}

/** Inverse van {@link timeToPx}. */
export function pxToTime(px: number, originMs: number, config: HdEphemerisZoomConfig): number {
  return originMs + px / config.pxPerMs;
}

/** Het zichtbare tijdvenster bij een scroll-positie en viewport-breedte. */
export function computeVisibleWindow(
  scrollLeftPx: number,
  viewportWidthPx: number,
  originMs: number,
  config: HdEphemerisZoomConfig,
): HdEphemerisRange {
  return {
    fromMs: pxToTime(scrollLeftPx, originMs, config),
    toMs: pxToTime(scrollLeftPx + viewportWidthPx, originMs, config),
  };
}

/** Begrenst een range op de ephemeris-dekking 1800–2399. */
export function clampRangeToDomain(range: HdEphemerisRange): HdEphemerisRange {
  return {
    fromMs: Math.max(HD_EPHEMERIS_DOMAIN_MIN_MS, range.fromMs),
    toMs: Math.min(HD_EPHEMERIS_DOMAIN_MAX_MS, range.toMs),
  };
}

export function unionRanges(a: HdEphemerisRange, b: HdEphemerisRange): HdEphemerisRange {
  return { fromMs: Math.min(a.fromMs, b.fromMs), toMs: Math.max(a.toMs, b.toMs) };
}

/** true als `outer` `inner` volledig dekt (dan hoeft er niet bijgeladen te worden). */
export function rangeCovers(outer: HdEphemerisRange, inner: HdEphemerisRange): boolean {
  return outer.fromMs <= inner.fromMs && outer.toMs >= inner.toMs;
}

/** Verbreedt een range aan beide kanten met `marginMs`. */
export function expandRange(range: HdEphemerisRange, marginMs: number): HdEphemerisRange {
  return { fromMs: range.fromMs - marginMs, toMs: range.toMs + marginMs };
}

/**
 * Het bereik van de eerste lading rond het anker, begrensd op het domein:
 * klassiek {@link HD_EPHEMERIS_BASE_RANGE_FACTOR} × het venster, gecentreerd;
 * met `baseMarginMs` (jaren) het beeld zoals het rond het anker komt te staan
 * plus die marge aan elke kant.
 */
export function computeBaseRange(
  anchorMs: number,
  viewportWidthPx: number,
  config: HdEphemerisZoomConfig,
): HdEphemerisRange {
  if (config.baseMarginMs == null) {
    const halfSpan = (config.windowMs * HD_EPHEMERIS_BASE_RANGE_FACTOR) / 2;
    return clampRangeToDomain({ fromMs: anchorMs - halfSpan, toMs: anchorMs + halfSpan });
  }
  const viewportMs = viewportWidthPx / config.pxPerMs;
  return clampRangeToDomain({
    fromMs: anchorMs - viewportMs * config.anchorFraction - config.baseMarginMs,
    toMs: anchorMs + viewportMs * (1 - config.anchorFraction) + config.baseMarginMs,
  });
}

// ── Clipping + projectie ─────────────────────────────────────────────────

/**
 * Behoudt alleen segmenten die het venster snijden, met clip-grenzen binnen
 * het venster. Het bronsegment (met échte grenzen) blijft ongemoeid voor de popup.
 */
export function clipSegmentsToWindow(
  segments: readonly HdTransitSegment[],
  window: HdEphemerisRange,
): HdEphemerisClippedSegment[] {
  const clipped: HdEphemerisClippedSegment[] = [];
  for (const segment of segments) {
    const fromMs = segment.fromUtc.valueOf();
    const toMs = segment.toUtc.valueOf();
    if (toMs <= window.fromMs || fromMs >= window.toMs) continue;
    clipped.push({
      segment,
      clipFromMs: Math.max(fromMs, window.fromMs),
      clipToMs: Math.min(toMs, window.toMs),
    });
  }
  return clipped;
}

/**
 * Projecteert geclipde segmenten naar het twee-niveaus-blokmodel (besluit 8):
 * aaneengesloten segmenten met dezelfde poort vormen één gate-container met de
 * lijn-items erbinnen. Bij gate-granulariteit (`line == null`) heeft elke
 * container geen lijn-items en is de container zelf het klikblok.
 * Labels staan nog op `null` — `adaptLabels`/`mergeTinyBlocks` volgen daarna.
 * `retrograde` (en het ℞ in het label) = de planeet loopt over het héle geclipte
 * blok retrograde volgens `retrogradeSpans`; een blok met een station erin krijgt
 * geen ℞, enkel zijn gedeeltelijke arcering (`flattenGateBlocks`).
 */
export function projectSegmentsToBlocks(
  clipped: readonly HdEphemerisClippedSegment[],
  originMs: number,
  config: HdEphemerisZoomConfig,
  retrogradeSpans: readonly HdTransitRetrogradeSpan[],
): HdEphemerisGateBlock<HdEphemerisLineBlock>[] {
  const blocks: HdEphemerisGateBlock<HdEphemerisLineBlock>[] = [];
  let group: HdEphemerisClippedSegment[] = [];
  const flush = (): void => {
    if (group.length > 0) {
      blocks.push(buildGateBlock(group, originMs, config, retrogradeSpans));
      group = [];
    }
  };
  for (const clip of clipped) {
    const previous = group[group.length - 1];
    if (previous != null && previous.segment.gate !== clip.segment.gate) flush();
    group.push(clip);
  }
  flush();
  return blocks;
}

function buildGateBlock(
  group: readonly HdEphemerisClippedSegment[],
  originMs: number,
  config: HdEphemerisZoomConfig,
  retrogradeSpans: readonly HdTransitRetrogradeSpan[],
): HdEphemerisGateBlock<HdEphemerisLineBlock> {
  const first = group[0];
  const last = group[group.length - 1];
  const gate = first.segment.gate;
  const xPx = timeToPx(first.clipFromMs, originMs, config);
  const retrograde = isRetrogradeThroughout(retrogradeSpans, first.clipFromMs, last.clipToMs);
  const lines: HdEphemerisLineBlock[] = group
    .filter(clip => clip.segment.line != null)
    .map(clip => {
      const lineX = timeToPx(clip.clipFromMs, originMs, config);
      const lineRetrograde = isRetrogradeThroughout(retrogradeSpans, clip.clipFromMs, clip.clipToMs);
      return {
        kind: 'line',
        id: `l-${gate}.${clip.segment.line}-${clip.segment.fromUtc.valueOf()}`,
        xPx: lineX,
        widthPx: Math.max(0.5, timeToPx(clip.clipToMs, originMs, config) - lineX),
        // Vol = "26.**1**", smal = ".**1**" — het lijnnummer is altijd het vette anker.
        fullLabel: {
          prefix: `${gate}.`,
          strong: `${clip.segment.line}`,
          suffix: lineRetrograde ? ' ℞' : undefined,
        },
        shortLabel: { prefix: '.', strong: `${clip.segment.line}` },
        label: null,
        retrograde: lineRetrograde,
        segment: clip.segment,
      };
    });
  return {
    kind: 'gate',
    id: `g-${gate}-${first.segment.fromUtc.valueOf()}`,
    gate,
    xPx,
    widthPx: Math.max(0.5, timeToPx(last.clipToMs, originMs, config) - xPx),
    fullLabel: { prefix: '', strong: `${gate}`, suffix: retrograde ? ' ℞' : undefined },
    shortLabel: { prefix: '', strong: `${gate}` },
    label: null,
    retrograde,
    fromUtc: first.segment.fromUtc,
    toUtc: last.segment.toUtc,
    lines,
  };
}

// ── Label-adaptiviteit + samenvoegen ─────────────────────────────────────

/**
 * Drie-traps labelresolutie (spec §4): past het volle label (`poort.lijn` —
 * met het lijnnummer als vet anker) dan dat, anders het smalle (`.lijn`),
 * anders `null` (kandidaat voor de merge). Labels zijn delen, geen platte
 * tekst: de template rendert ze als `<text>` met `<tspan>`s.
 */
export function resolveBlockLabel(
  widthPx: number,
  fullLabel: HdEphemerisLabelParts,
  shortLabel: HdEphemerisLabelParts,
  fullMinPx = HD_EPHEMERIS_FULL_LABEL_MIN_PX,
  shortMinPx = HD_EPHEMERIS_SHORT_LABEL_MIN_PX,
): HdEphemerisLabelParts | null {
  if (widthPx >= fullMinPx) return fullLabel;
  if (widthPx >= shortMinPx) return shortLabel;
  return null;
}

/** Zet `label` op elk blok via {@link resolveBlockLabel} (immutable). */
export function adaptLabels<T extends HdEphemerisLabeledBlock>(
  blocks: readonly T[],
  fullMinPx = HD_EPHEMERIS_FULL_LABEL_MIN_PX,
  shortMinPx = HD_EPHEMERIS_SHORT_LABEL_MIN_PX,
): T[] {
  return blocks.map(block => {
    const label = resolveBlockLabel(block.widthPx, block.fullLabel, block.shortLabel, fullMinPx, shortMinPx);
    return label === block.label ? block : { ...block, label };
  });
}

export function isMergedBlock<T>(block: T | HdEphemerisMergedBlock<T>): block is HdEphemerisMergedBlock<T> {
  return (block as HdEphemerisMergedBlock<T>).kind === 'merged';
}

/**
 * Voegt aaneengesloten blokken smaller dan `minWidthPx` samen tot één
 * "…"-blok (met `count` + de items voor aria en popup). Eén los smal blok
 * tussen brede blokken blijft gewoon staan (zonder label, zie `adaptLabels`).
 */
export function mergeTinyBlocks<T extends { readonly id: string; readonly xPx: number; readonly widthPx: number }>(
  blocks: readonly T[],
  minWidthPx = HD_EPHEMERIS_MERGE_MIN_PX,
): (T | HdEphemerisMergedBlock<T>)[] {
  const result: (T | HdEphemerisMergedBlock<T>)[] = [];
  let run: T[] = [];
  const flush = (): void => {
    if (run.length >= 2) {
      const first = run[0];
      const last = run[run.length - 1];
      result.push({
        kind: 'merged',
        id: `m-${first.id}-x${run.length}`,
        xPx: first.xPx,
        widthPx: Math.max(0.5, last.xPx + last.widthPx - first.xPx),
        count: run.length,
        items: [...run],
      });
    } else {
      result.push(...run);
    }
    run = [];
  };
  for (const block of blocks) {
    if (block.widthPx < minWidthPx) {
      run.push(block);
      continue;
    }
    flush();
    result.push(block);
  }
  flush();
  return result;
}

/**
 * Maakt de geneste blokstructuur plat tot absolute render-items voor de
 * template (gate = container-band, lijn = item onderin de container,
 * merged = "…"-blok op het niveau waar de merge plaatsvond). Elk item krijgt
 * als arcering zijn doorsnede met `retrogradePx` ({@link projectRetrogradeSpans}).
 */
export function flattenGateBlocks(
  blocks: readonly (HdEphemerisGateBlock | HdEphemerisMergedBlock<HdEphemerisGateBlock>)[],
  retrogradePx: readonly HdEphemerisPxSpan[],
): HdEphemerisRenderItem[] {
  const items: HdEphemerisRenderItem[] = [];
  const hatches = (xPx: number, widthPx: number): HdEphemerisPxSpan[] => blockRetroHatches(xPx, widthPx, retrogradePx);
  for (const block of blocks) {
    if (isMergedBlock(block)) {
      const first = block.items[0];
      items.push({
        id: block.id,
        kind: 'merged',
        xPx: block.xPx,
        yPx: HD_EPHEMERIS_GATE_Y_PX,
        widthPx: block.widthPx,
        heightPx: HD_EPHEMERIS_GATE_HEIGHT_PX,
        label: { prefix: '', strong: '…' },
        labelXPx: block.xPx + block.widthPx / 2,
        labelYPx: HD_EPHEMERIS_GATE_LABEL_Y_PX,
        labelAnchor: 'middle',
        retrograde: false,
        hatches: hatches(block.xPx, block.widthPx),
        gate: first.gate,
        line: null,
        fromUtc: first.fromUtc,
        toUtc: first.toUtc,
        count: block.count,
        containerId: block.id,
        ariaLabel: null,
      });
      continue;
    }
    items.push({
      id: block.id,
      kind: 'gate',
      xPx: block.xPx,
      yPx: HD_EPHEMERIS_GATE_Y_PX,
      widthPx: block.widthPx,
      heightPx: HD_EPHEMERIS_GATE_HEIGHT_PX,
      label: block.label,
      labelXPx: block.xPx + 3,
      labelYPx: HD_EPHEMERIS_GATE_LABEL_Y_PX,
      labelAnchor: 'start',
      retrograde: block.retrograde,
      hatches: hatches(block.xPx, block.widthPx),
      gate: block.gate,
      line: null,
      fromUtc: block.fromUtc,
      toUtc: block.toUtc,
      count: 1,
      containerId: block.id,
      ariaLabel: null,
    });
    for (const line of block.lines) {
      if (isMergedBlock(line)) {
        const first = line.items[0];
        items.push({
          id: line.id,
          kind: 'merged',
          xPx: line.xPx,
          yPx: HD_EPHEMERIS_LINE_Y_PX,
          widthPx: line.widthPx,
          heightPx: HD_EPHEMERIS_LINE_HEIGHT_PX,
          label: { prefix: '', strong: '…' },
          labelXPx: line.xPx + line.widthPx / 2,
          labelYPx: HD_EPHEMERIS_LINE_Y_PX + 13,
          labelAnchor: 'middle',
          retrograde: false,
          hatches: hatches(line.xPx, line.widthPx),
          gate: first.segment.gate,
          line: first.segment.line,
          fromUtc: first.segment.fromUtc,
          toUtc: first.segment.toUtc,
          count: line.count,
          containerId: block.id,
          ariaLabel: null,
        });
      } else {
        items.push({
          id: line.id,
          kind: 'line',
          xPx: line.xPx,
          yPx: HD_EPHEMERIS_LINE_Y_PX,
          widthPx: line.widthPx,
          heightPx: HD_EPHEMERIS_LINE_HEIGHT_PX,
          label: line.label,
          labelXPx: line.xPx + line.widthPx / 2,
          labelYPx: HD_EPHEMERIS_LINE_Y_PX + 13,
          labelAnchor: 'middle',
          retrograde: line.retrograde,
          hatches: hatches(line.xPx, line.widthPx),
          gate: line.segment.gate,
          line: line.segment.line,
          fromUtc: line.segment.fromUtc,
          toUtc: line.segment.toUtc,
          count: 1,
          containerId: block.id,
          ariaLabel: null,
        });
      }
    }
  }
  return items;
}

/** Breedte (px) van de onzichtbare hover-zone rond een blok-overgang. */
export const HD_EPHEMERIS_BOUNDARY_HIT_WIDTH_PX = 4;

/**
 * Overgangen tussen aansluitende blokken: per band (poort-band en lijn-band)
 * gezocht en daarna ontdubbeld op x — een poort-wissel valt immers altijd
 * samen met een lijn-wissel. `atUtc` is de echte starttijd van het rechter
 * blok; de geklipte buitenranden van het venster leveren geen grens op omdat
 * ze geen voorganger hebben.
 */
export function computeBlockBoundaries(
  items: readonly HdEphemerisRenderItem[],
  epsilonPx = 0.5,
): HdEphemerisBoundaryMarker[] {
  const bands = groupItemsByBand(items);
  const boundaryByX = new Map<number, HdEphemerisBoundaryMarker>();
  for (const band of bands) {
    for (let index = 1; index < band.length; index++) {
      const previous = band[index - 1];
      const current = band[index];
      if (Math.abs(current.xPx - (previous.xPx + previous.widthPx)) > epsilonPx) continue;
      const key = Math.round(current.xPx);
      if (boundaryByX.has(key)) continue;
      boundaryByX.set(key, { id: `boundary-${key}`, xPx: current.xPx, atUtc: current.fromUtc });
    }
  }
  return [...boundaryByX.values()].sort((first, second) => first.xPx - second.xPx);
}

/** Splitst de platte render-items in horizontale banden (poort-band, lijn-band). */
function groupItemsByBand(items: readonly HdEphemerisRenderItem[]): HdEphemerisRenderItem[][] {
  const bandByY = new Map<number, HdEphemerisRenderItem[]>();
  for (const item of items) {
    const band = bandByY.get(item.yPx);
    if (band) band.push(item);
    else bandByY.set(item.yPx, [item]);
  }
  return [...bandByY.values()];
}

// ── Retrograde: lussen + stations ────────────────────────────────────────

/**
 * Detecteert retro-lussen (spec §8): een poort die later opnieuw betreden
 * wordt mét een retrograde-passage ertussen (het her-betreden segment zelf is
 * retro, of een segment ertussen). De boog loopt van het einde van het eerdere
 * segment naar het begin van het latere. Gewone aaneensluiting (andere poort,
 * of her-betreding zonder retro) geeft géén lus. Verwacht segmenten op
 * oplopende `fromUtc` (service-garantie).
 */
export function detectRetroLoops(segments: readonly HdTransitSegment[]): HdEphemerisRetroLoopSpan[] {
  const loops: HdEphemerisRetroLoopSpan[] = [];
  for (let i = 0; i < segments.length; i++) {
    for (let j = i + 1; j < segments.length; j++) {
      if (segments[j].gate !== segments[i].gate) continue;
      const hasRetroPassage =
        segments[j].retrograde || segments.slice(i + 1, j).some(between => between.retrograde);
      if (hasRetroPassage) {
        loops.push({
          gate: segments[i].gate,
          fromMs: segments[i].toUtc.valueOf(),
          toMs: segments[j].fromUtc.valueOf(),
        });
      }
      break; // alleen de eerstvolgende her-betreding van deze poort
    }
  }
  return loops;
}

/** Subtiele boog voor een retro-lus: kwadratische bezier die `depthPx` onder de basislijn hangt. */
export function retroLoopPath(
  x1Px: number,
  x2Px: number,
  yPx = HD_EPHEMERIS_LOOP_Y_PX,
  depthPx = HD_EPHEMERIS_LOOP_DEPTH_PX,
): string {
  const midX = (x1Px + x2Px) / 2;
  return `M ${x1Px} ${yPx} Q ${midX} ${yPx + depthPx * 2} ${x2Px} ${yPx}`;
}

/**
 * Projecteert de exacte retrograde-perioden binnen het venster naar px-stukken
 * op de lane (oplopend): de arcering begint en eindigt zo op het station zelf.
 */
export function projectRetrogradeSpans(
  spans: readonly HdTransitRetrogradeSpan[],
  window: HdEphemerisRange,
  originMs: number,
  config: HdEphemerisZoomConfig,
): HdEphemerisPxSpan[] {
  return clipRetrogradeSpans(spans, window.fromMs, window.toMs).map(span => {
    const xPx = timeToPx(span.fromMs, originMs, config);
    return { xPx, widthPx: timeToPx(span.toMs, originMs, config) - xPx };
  });
}

/** Arcering van één blok: de doorsnede van [xPx, xPx + widthPx] met de retrograde-px-stukken. */
export function blockRetroHatches(
  xPx: number,
  widthPx: number,
  retrogradePx: readonly HdEphemerisPxSpan[],
): HdEphemerisPxSpan[] {
  const endPx = xPx + widthPx;
  const hatches: HdEphemerisPxSpan[] = [];
  for (const span of retrogradePx) {
    const fromPx = Math.max(xPx, span.xPx);
    const toPx = Math.min(endPx, span.xPx + span.widthPx);
    if (toPx > fromPx) hatches.push({ xPx: fromPx, widthPx: toPx - fromPx });
  }
  return hatches;
}

/** Projecteert stations binnen het venster naar px-posities op de lane. */
export function projectStations(
  stations: readonly HdTransitStation[],
  window: HdEphemerisRange,
  originMs: number,
  config: HdEphemerisZoomConfig,
): HdEphemerisStationMarker[] {
  return stations
    .filter(station => {
      const ms = station.atUtc.valueOf();
      return ms >= window.fromMs && ms < window.toMs;
    })
    .map(station => ({
      id: `s-${station.atUtc.valueOf()}`,
      xPx: timeToPx(station.atUtc.valueOf(), originMs, config),
      turnsTo: station.turnsTo,
      atUtc: station.atUtc,
    }));
}

// ── Jaren-weergave: poortperiodes van trage planeten ─────────────────────

/** Rij (0/1) per poort: de pariteit van zijn plek op het wiel. */
const GATE_WHEEL_ROW = new Map<number, number>(MANDALA_GATE_SEQUENCE_CCW.map((gate, index) => [gate, index % 2]));

/**
 * Rij van een poortperiode-blok. Opeenvolgende poortperiodes overlappen in de
 * tijd (de planeet gaat retrograde heen en weer over de grens) en zijn buren op
 * het wiel, dus de wielpariteit laat ze altijd verspringen. Bewust niet de
 * volgorde in de geladen lijst: bijladen aan de linkerkant zou dan alle blokken
 * van rij doen wisselen. Het wiel heeft 64 poorten (even), dus ook de overgang
 * 44 → 1 … wisselt van rij. Periodes van poort g en g+2 overlappen nooit: de
 * retrograde boog is kleiner dan een poort.
 */
export function gateWheelRow(gate: number): number {
  return GATE_WHEEL_ROW.get(gate) ?? 0;
}

/** Bovenkant (px, binnen de lane) van een rij poortperiode-blokken. */
export function yearRowYPx(row: number): number {
  return HD_EPHEMERIS_YEAR_LANE_PADDING_PX + row * (HD_EPHEMERIS_YEAR_BOX_HEIGHT_PX + HD_EPHEMERIS_YEAR_ROW_GAP_PX);
}

/** De jaren-weergave kan enkel als alle zichtbare lanes transpersoonlijke planeten zijn. */
export function yearZoomAvailable(visiblePlanetKeys: readonly HdPlanetKey[]): boolean {
  return visiblePlanetKeys.length > 0 && visiblePlanetKeys.every(key => legendCategoryOfKey(key) === 'outer-trans');
}

interface HdGateEpochDraft {
  readonly gate: number;
  readonly fromUtc: Moment;
  toUtc: Moment;
  readonly visits: HdEphemerisRange[];
}

/**
 * Bundelt poort-segmenten (oplopend, aaneensluitend) tot poortperiodes: elke
 * doorgang door een poort die binnen `maxGapMs` na de vorige doorgang van
 * dezelfde poort begint, hoort bij dezelfde periode. Zo wordt 26 · 11 · 26 ·
 * 11 · 26 · 11 (Pluto die retrograde heen en weer gaat) twee periodes: 26 tot
 * de laatste uittrede en 11 vanaf de eerste intrede, die elkaar overlappen.
 *
 * `startKnown`/`endKnown`: de data dekt [eerste segmentstart, laatste
 * segmenteinde]; een periode die binnen `maxGapMs` van die rand begint (of
 * eindigt) kan er nog een doorgang buiten hebben.
 */
export function buildGateEpochs(
  segments: readonly HdTransitSegment[],
  maxGapMs = HD_EPHEMERIS_GATE_EPOCH_MAX_GAP_MS,
): HdEphemerisGateEpoch[] {
  if (segments.length === 0) return [];
  const coverageFromMs = segments[0].fromUtc.valueOf();
  const coverageToMs = segments[segments.length - 1].toUtc.valueOf();
  const openByGate = new Map<number, HdGateEpochDraft>();
  const closed: HdGateEpochDraft[] = [];
  for (const segment of segments) {
    const visit = { fromMs: segment.fromUtc.valueOf(), toMs: segment.toUtc.valueOf() };
    const open = openByGate.get(segment.gate);
    if (open != null && visit.fromMs - open.toUtc.valueOf() <= maxGapMs) {
      open.visits.push(visit);
      open.toUtc = segment.toUtc;
      continue;
    }
    if (open != null) closed.push(open);
    openByGate.set(segment.gate, { gate: segment.gate, fromUtc: segment.fromUtc, toUtc: segment.toUtc, visits: [visit] });
  }
  return [...closed, ...openByGate.values()]
    .sort((first, second) => first.fromUtc.valueOf() - second.fromUtc.valueOf())
    .map(draft => ({
      gate: draft.gate,
      fromUtc: draft.fromUtc,
      toUtc: draft.toUtc,
      visits: draft.visits,
      startKnown: draft.fromUtc.valueOf() - maxGapMs >= coverageFromMs,
      endKnown: draft.toUtc.valueOf() + maxGapMs <= coverageToMs,
    }));
}

/**
 * Projecteert de poortperiodes die de cliprange raken naar blokken op de lane
 * (niet geclipt: een blok loopt door tot zijn échte randen), met de rij uit
 * {@link gateWheelRow} en de labelvariant die in de breedte past.
 */
export function projectGateEpochs(
  epochs: readonly HdEphemerisGateEpoch[],
  clipRange: HdEphemerisRange,
  originMs: number,
  config: HdEphemerisZoomConfig,
): HdEphemerisYearBox[] {
  return epochs
    .filter(epoch => epoch.toUtc.valueOf() > clipRange.fromMs && epoch.fromUtc.valueOf() < clipRange.toMs)
    .map(epoch => {
      const fromMs = epoch.fromUtc.valueOf();
      const xPx = timeToPx(fromMs, originMs, config);
      const widthPx = Math.max(1, timeToPx(epoch.toUtc.valueOf(), originMs, config) - xPx);
      const row = gateWheelRow(epoch.gate);
      return {
        id: `y-${epoch.gate}-${fromMs}`,
        epoch,
        row,
        xPx,
        yPx: yearRowYPx(row),
        widthPx,
        heightPx: HD_EPHEMERIS_YEAR_BOX_HEIGHT_PX,
        visits: epoch.visits.map(visit => {
          const visitXPx = timeToPx(visit.fromMs, originMs, config);
          return { xPx: visitXPx, widthPx: Math.max(0.5, timeToPx(visit.toMs, originMs, config) - visitXPx) };
        }),
        labelXPx: xPx + widthPx / 2,
        titleTier: yearTitleTier(widthPx),
        datesTier: yearDatesTier(widthPx),
      };
    });
}

function yearTitleTier(widthPx: number): HdEphemerisYearTitleTier {
  if (widthPx >= HD_EPHEMERIS_YEAR_TITLE_FULL_MIN_PX) return 'full';
  return widthPx >= HD_EPHEMERIS_YEAR_TITLE_SHORT_MIN_PX ? 'short' : null;
}

function yearDatesTier(widthPx: number): HdEphemerisYearDatesTier {
  if (widthPx >= HD_EPHEMERIS_YEAR_DATES_FULL_MIN_PX) return 'full';
  if (widthPx >= HD_EPHEMERIS_YEAR_DATES_SHORT_MIN_PX) return 'short';
  return widthPx >= HD_EPHEMERIS_YEAR_DATES_YEARS_MIN_PX ? 'years' : null;
}

// ── Ruler: datum/tijd-ticks per zoom-niveau ──────────────────────────────

/**
 * Stap-kandidaat voor de ruler: een kalender-stap (oplopend qua grootte) met
 * label-formaat en major-regel. De dichtstbijzijnde stap die de labels nog
 * genoeg px-ruimte geeft wint (adaptief uitdunnen).
 */
interface HdEphemerisRulerStep {
  readonly unit: 'hour' | 'day' | 'week' | 'month' | 'year';
  readonly count: number;
  /** Benaderde stapgrootte voor de px-schatting (kalendereenheden variëren). */
  readonly approxMs: number;
  readonly format: (at: Moment) => string;
  readonly isMajor: (at: Moment) => boolean;
  /**
   * Optionele fijnere tussenstap zonder label: korte streepjes die de
   * gelabelde stap onderverdelen (bv. 2-uur-streepjes bij 4-uur-labels).
   */
  readonly minor?: { readonly unit: 'hour' | 'day' | 'month'; readonly count: number };
}

/** Jaar-stap van de jaren-weergave: major op elk decennium (bij de 10-jaarstap: elke halve eeuw). */
function yearRulerStep(count: number): HdEphemerisRulerStep {
  const majorEvery = count >= 10 ? 50 : 10;
  return {
    unit: 'year',
    count,
    approxMs: count * MS_PER_YEAR,
    format: (at: Moment) => at.format('YYYY'),
    isMajor: (at: Moment) => at.year() % majorEvery === 0,
    // Kwartaalstreepjes zonder label tussen de jaartallen.
    minor: count === 1 ? { unit: 'month', count: 3 } : undefined,
  };
}

/** Lokale weekaanduiding, bv. "week 31" (taal-neutraal genoeg — geen i18n-key). */
function formatRulerWeek(at: Moment): string {
  return `week ${at.isoWeek()}`;
}

/**
 * Kandidaten per zoom-niveau, van fijn naar grof. Major markeert de eerste
 * grotere grens: middernacht (uur), de 1e van de maand (dag), de eerste
 * week van de maand (weekticks vallen op maandagen: dag ≤ 7), januari (maand).
 */
const RULER_STEP_CANDIDATES: Readonly<Record<HdEphemerisZoomLevel, readonly HdEphemerisRulerStep[]>> = {
  hour: [1, 2, 6, 12].map(count => ({
    unit: 'hour' as const,
    count,
    approxMs: count * MS_PER_HOUR,
    // Dag-wissel: om middernacht staat het datumlabel in plaats van "00:00".
    format: (at: Moment) => (at.hour() === 0 ? at.format('ddd D MMM') : at.format('HH:mm')),
    isMajor: (at: Moment) => at.hour() === 0,
  })),
  day: [
    // Op de standaard dag-dichtheid (240 px/dag) is er ruimte voor uur-ticks:
    // 4 uur = 40 px. Het datumlabel staat dan op middernacht, kort ("25 jul")
    // omdat de tussenliggende uurlabels de ruimte delen. De 4-uur-stap krijgt
    // ongelabelde streepjes per 2 uur voor een fijnere tijdsaflezing.
    ...[4, 6, 12].map(count => ({
      unit: 'hour' as const,
      count,
      approxMs: count * MS_PER_HOUR,
      format: (at: Moment) => (at.hour() === 0 ? at.format('D MMM') : at.format('HH:mm')),
      isMajor: (at: Moment) => at.hour() === 0,
      minor: count === 4 ? ({ unit: 'hour', count: 2 } as const) : undefined,
    })),
    {
      unit: 'day',
      count: 1,
      approxMs: MS_PER_DAY,
      format: (at: Moment) => at.format('ddd D MMM'),
      isMajor: (at: Moment) => at.date() === 1,
    },
    {
      unit: 'day',
      count: 2,
      approxMs: 2 * MS_PER_DAY,
      format: (at: Moment) => at.format('ddd D MMM'),
      isMajor: (at: Moment) => at.date() === 1,
    },
    {
      unit: 'week',
      count: 1,
      approxMs: 7 * MS_PER_DAY,
      format: formatRulerWeek,
      isMajor: (at: Moment) => at.date() <= 7,
    },
  ],
  week: [
    {
      unit: 'day',
      count: 1,
      approxMs: MS_PER_DAY,
      format: (at: Moment) => at.format('D MMM'),
      isMajor: (at: Moment) => at.date() === 1,
    },
    {
      unit: 'day',
      count: 2,
      approxMs: 2 * MS_PER_DAY,
      format: (at: Moment) => at.format('D MMM'),
      isMajor: (at: Moment) => at.date() === 1,
    },
    {
      unit: 'week',
      count: 1,
      approxMs: 7 * MS_PER_DAY,
      format: formatRulerWeek,
      isMajor: (at: Moment) => at.date() <= 7,
    },
  ],
  month: [
    {
      unit: 'month',
      count: 1,
      approxMs: 30.4375 * MS_PER_DAY,
      format: (at: Moment) => at.format('MMM YYYY'),
      isMajor: (at: Moment) => at.month() === 0,
    },
    {
      unit: 'month',
      count: 3,
      approxMs: 3 * 30.4375 * MS_PER_DAY,
      format: (at: Moment) => at.format('MMM YYYY'),
      isMajor: (at: Moment) => at.month() === 0,
    },
    {
      unit: 'year',
      count: 1,
      approxMs: 365.25 * MS_PER_DAY,
      format: (at: Moment) => at.format('YYYY'),
      isMajor: () => true,
    },
  ],
  year: [1, 2, 5, 10].map(yearRulerStep),
};

/**
 * Anker waar de tick-stap aan uitgelijnd wordt, zodat de ticks op vaste
 * kalendergrenzen vallen (en niet meeschuiven met de scroll-positie):
 * uren delen de dag, dagen starten op de 1e van de maand, weekticks op
 * maandag (ISO), maanden op het jaar, meerjarige stappen op een veelvoud van
 * de stap (2000, 2005, 2010 …).
 */
function rulerStepAnchor(fromMs: number, unit: HdEphemerisRulerStep['unit'], count: number): Moment {
  const at = moment(fromMs); // lokale tijd — de ruler toont lokale datum/tijd
  switch (unit) {
    case 'hour':
      return at.startOf('day');
    case 'day':
      return at.startOf('month');
    case 'week':
      return at.startOf('isoWeek');
    case 'month':
      return at.startOf('year');
    case 'year':
      return at.startOf('year').year(Math.floor(at.year() / count) * count);
  }
}

/**
 * Ticks voor de datum/tijd-ruler op het zichtbare venster: kiest per
 * zoom-niveau de fijnste stap met minstens `HD_EPHEMERIS_RULER_MIN_LABEL_SPACING_PX`
 * tussen de ticks en loopt de kalendergrenzen af. `xPx` is relatief aan
 * `window.fromMs`; er wordt één stap vóór het venster meegenomen zodat ook de
 * linkerrand een label heeft (de ruler-viewport clipt wat buiten beeld valt).
 */
export function computeRulerTicks(
  window: HdEphemerisRange,
  level: HdEphemerisZoomLevel,
  pxPerMs: number,
): HdEphemerisRulerTick[] {
  const step = selectRulerStep(level, pxPerMs);
  const labelled = walkRulerSteps(window, step.unit, step.count, step.approxMs).map(at => ({
    timeMs: at.valueOf(),
    xPx: (at.valueOf() - window.fromMs) * pxPerMs,
    label: step.format(at),
    major: step.isMajor(at),
    tooltip: formatRulerTooltip(at, step.unit),
  }));
  const minorStep = step.minor;
  if (!minorStep) return labelled;

  const labelledTimes = new Set(labelled.map(tick => tick.timeMs));
  const minor = walkRulerSteps(window, minorStep.unit, minorStep.count, step.approxMs)
    .filter(at => !labelledTimes.has(at.valueOf()))
    .map(at => ({
      timeMs: at.valueOf(),
      xPx: (at.valueOf() - window.fromMs) * pxPerMs,
      label: null,
      major: false,
      tooltip: formatRulerTooltip(at, minorStep.unit),
    }));
  return [...labelled, ...minor].sort((left, right) => left.timeMs - right.timeMs);
}

/** Fijnste stap die de labels nog minstens de minimale px-ruimte geeft. */
function selectRulerStep(level: HdEphemerisZoomLevel, pxPerMs: number): HdEphemerisRulerStep {
  const candidates = RULER_STEP_CANDIDATES[level];
  return (
    candidates.find(candidate => candidate.approxMs * pxPerMs >= HD_EPHEMERIS_RULER_MIN_LABEL_SPACING_PX) ??
    candidates[candidates.length - 1]
  );
}

/**
 * Loopt de kalendergrenzen van één stap af over het venster. `marginMs` bepaalt
 * hoever vóór het venster nog meegenomen wordt (één gelabelde stap, zodat ook
 * de linkerrand een label heeft).
 */
function walkRulerSteps(
  window: HdEphemerisRange,
  unit: HdEphemerisRulerStep['unit'],
  count: number,
  marginMs: number,
): Moment[] {
  const moments: Moment[] = [];
  for (const at = rulerStepAnchor(window.fromMs, unit, count); at.valueOf() <= window.toMs; at.add(count, unit)) {
    if (at.valueOf() < window.fromMs - marginMs) continue;
    moments.push(at.clone());
  }
  return moments;
}

/** Volledige lokale datum/tijd voor de hover-tooltip op een tick-streepje. */
function formatRulerTooltip(at: Moment, unit: HdEphemerisRulerStep['unit']): string {
  if (unit === 'year') return at.format('YYYY');
  return unit === 'hour' ? at.format('ddd D MMM YYYY HH:mm') : at.format('ddd D MMM YYYY');
}

// ── Kleur ────────────────────────────────────────────────────────────────

/** `'#ffb800'` + alpha → `'rgba(255, 184, 0, 0.12)'` — tint voor gate/lijn-achtergronden. */
export function hexToRgba(hex: string, alpha: number): string {
  const value = parseInt(hex.replace('#', ''), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
